// share-images.js — canvas-rendered shareable images (full scorecard, player
// stats card) + a native-safe save helper for ANY canvas (posters included).
import { saveBlobToDevice, shareSavedFile } from './platform-save.js';
import { formatOvers } from './scoring-logic.js';

const FONT = (w, s) => `${w} ${s}px Outfit, -apple-system, sans-serif`;

/**
 * Load an image for canvas compositing without leaking it.
 *
 * The old inline `new Image()` blocks left onload/onerror closures holding the
 * element (and through it the decoded bitmap) alive, and had NO timeout — a
 * request that neither loaded nor errored (a CORS-blocked avatar, a stalled
 * CDN) left the export promise pending forever and the canvas un-collectable.
 * Resolves with the image, or null on failure/timeout.
 */
function loadImageSafely(src, timeoutMs = 6000) {
  return new Promise(resolve => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    let settled = false;
    const done = (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      if (!value) { try { img.src = ''; } catch (_) {} }
      resolve(value);
    };
    const timer = setTimeout(() => {
      console.warn('[ShareImage] avatar load timed out:', String(src).slice(0, 80));
      done(null);
    }, timeoutMs);
    img.onload = () => done(img);
    img.onerror = () => done(null);
    try { img.src = src; } catch (_) { done(null); }
  });
}

/** Save a canvas as PNG on web AND native (share sheet on Android). */
export function saveCanvasImage(canvas, fileName) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Image render timed out')), 10000);
    try {
      canvas.toBlob(async blob => {
        clearTimeout(timer);
        if (!blob) { reject(new Error('Image render failed')); return; }
        try {
          const result = await saveBlobToDevice(blob, fileName);
          if (result.ok && result.uri) await shareSavedFile(result.uri, fileName);
          resolve(result);
        } catch (e) {
          reject(e);
        }
      }, 'image/png');
    } catch (e) {
      clearTimeout(timer);
      reject(e);
    }
  }).catch(error => ({ ok: false, error }));
}

function dismissalText(b) {
  if (!b.out) return (b.balls > 0 || b.runs > 0) ? 'not out' : 'did not bat';
  switch (b.outType) {
    case 'RunOut': return 'run out';
    case 'Caught': return b.outFielder ? `c ${b.outFielder}` : `c (b ${b.outBowler || ''})`;
    case 'Stumped': return `st (b ${b.outBowler || ''})`;
    default: return b.outBowler ? `${String(b.outType || 'out').toLowerCase()} b ${b.outBowler}` : String(b.outType || 'out').toLowerCase();
  }
}

function clip(ctx, text, max) {
  let t = String(text || '');
  while (ctx.measureText(t).width > max && t.length > 1) t = t.slice(0, -1);
  return t.length < String(text || '').length ? t.replace(/.$/, '…') : t;
}

/**
 * Full match scorecard (both innings, batting + bowling) as a canvas.
 * Height adapts to content. Returns the canvas.
 */
export function renderScorecardCanvas(match, teamColorFor) {
  const W = 1080;
  const innBlocks = match.innings.map(inn => {
    const bats = inn.batsmen.filter(b => b.balls > 0 || b.runs > 0 || b.out);
    const bowls = inn.bowlers.filter(b => b.balls > 0);
    return { inn, bats, bowls };
  });
  let H = 250; // header
  innBlocks.forEach(({ bats, bowls }) => { H += 120 + (bats.length + 1) * 40 + 60 + (bowls.length + 1) * 40 + 40; });
  H += 70; // footer

  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#0f172a');
  grad.addColorStop(1, '#020617');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#94a3b8';
  ctx.font = FONT(900, 26);
  ctx.fillText('FULL SCORECARD', W / 2, 50);

  ctx.fillStyle = '#fff';
  ctx.font = FONT(900, 46);
  ctx.fillText(clip(ctx, `${match.teamA.name}  vs  ${match.teamB.name}`, W - 100), W / 2, 92);

  ctx.fillStyle = '#64748b';
  ctx.font = FONT(700, 22);
  const dateStr = match.date ? new Date(match.date).toLocaleDateString() : '';
  ctx.fillText(`${match.overs} overs · ${dateStr}`, W / 2, 150);

  // Result line
  if (match.result) {
    ctx.fillStyle = '#fbbf24';
    ctx.font = FONT(800, 26);
    let line = 'Match tied';
    if (match.result.winner === 'A' || match.result.winner === 'B') {
      const wname = match.result.winner === 'A' ? match.teamA.name : match.teamB.name;
      line = `${wname} won${match.result.margin ? ` by ${match.result.margin} ${match.result.marginType}` : ''}`;
    } else if (match.result.winner === 'COMPLETED') line = 'Match completed';
    ctx.fillText(clip(ctx, line, W - 100), W / 2, 188);
  }

  let y = 250;
  const L = 70, R = W - 70;

  innBlocks.forEach(({ inn, bats, bowls }) => {
    const teamName = inn.battingTeam === 'A' ? match.teamA.name : match.teamB.name;
    const color = (teamColorFor && teamColorFor(teamName)) || '#10b981';

    // Innings header bar
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.18;
    ctx.fillRect(L - 20, y, W - 2 * (L - 20), 56);
    ctx.globalAlpha = 1;
    ctx.textAlign = 'left';
    ctx.fillStyle = '#fff';
    ctx.font = FONT(900, 30);
    ctx.fillText(clip(ctx, teamName.toUpperCase(), 640), L, y + 12);
    ctx.textAlign = 'right';
    const extras = inn.extras || {};
    const extrasTotal = (extras.wide || 0) + (extras.nb || 0) + (extras.b || 0) + (extras.lb || 0);
    ctx.fillText(`${inn.totalRuns}/${inn.wickets}  (${formatOvers(inn.balls)})`, R, y + 12);
    y += 80;

    // Batting table
    const cols = { name: L, dis: L + 330, r: R - 300, b: R - 220, f4: R - 140, s6: R - 70, sr: R };
    ctx.font = FONT(800, 20);
    ctx.fillStyle = '#64748b';
    ctx.textAlign = 'left';
    ctx.fillText('BATTER', cols.name, y);
    ctx.fillText('DISMISSAL', cols.dis, y);
    ctx.textAlign = 'right';
    ctx.fillText('R', cols.r, y); ctx.fillText('B', cols.b, y);
    ctx.fillText('4s', cols.f4, y); ctx.fillText('6s', cols.s6, y); ctx.fillText('SR', cols.sr, y);
    y += 40;

    bats.forEach(b => {
      const name = b.name || 'Unknown';
      const sr = b.balls > 0 ? ((b.runs / b.balls) * 100).toFixed(1) : '0.0';
      ctx.strokeStyle = 'rgba(148,163,184,0.12)';
      ctx.beginPath(); ctx.moveTo(L, y - 8); ctx.lineTo(R, y - 8); ctx.stroke();
      ctx.textAlign = 'left';
      ctx.fillStyle = b.out ? '#e2e8f0' : '#34d399';
      ctx.font = FONT(800, 24);
      ctx.fillText(clip(ctx, name + (b.out ? '' : ' *'), 310), cols.name, y);
      ctx.fillStyle = '#94a3b8';
      ctx.font = FONT(600, 20);
      ctx.fillText(clip(ctx, dismissalText(b), 350), cols.dis, y + 2);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#fff';
      ctx.font = FONT(900, 24);
      ctx.fillText(String(b.runs), cols.r, y);
      ctx.fillStyle = '#cbd5e1';
      ctx.font = FONT(700, 22);
      ctx.fillText(String(b.balls), cols.b, y);
      ctx.fillText(String(b.fours), cols.f4, y);
      ctx.fillText(String(b.sixes), cols.s6, y);
      ctx.fillText(sr, cols.sr, y);
      y += 40;
    });

    // Extras
    ctx.textAlign = 'left';
    ctx.fillStyle = '#64748b';
    ctx.font = FONT(700, 20);
    ctx.fillText(`Extras: ${extrasTotal}  (wd ${extras.wide || 0}, nb ${extras.nb || 0}, b ${extras.b || 0}, lb ${extras.lb || 0})`, L, y);
    y += 60;

    // Bowling table
    const bc = { name: L, o: R - 300, r: R - 200, w: R - 100, e: R };
    ctx.fillStyle = '#64748b';
    ctx.font = FONT(800, 20);
    ctx.fillText('BOWLER', bc.name, y);
    ctx.textAlign = 'right';
    ctx.fillText('O', bc.o, y); ctx.fillText('R', bc.r, y); ctx.fillText('W', bc.w, y); ctx.fillText('ECON', bc.e, y);
    y += 40;
    bowls.forEach(b => {
      ctx.strokeStyle = 'rgba(148,163,184,0.12)';
      ctx.beginPath(); ctx.moveTo(L, y - 8); ctx.lineTo(R, y - 8); ctx.stroke();
      ctx.textAlign = 'left';
      ctx.fillStyle = '#e2e8f0';
      ctx.font = FONT(800, 24);
      ctx.fillText(clip(ctx, b.name, 500), bc.name, y);
      ctx.textAlign = 'right';
      ctx.fillStyle = '#cbd5e1';
      ctx.font = FONT(700, 22);
      ctx.fillText(formatOvers(b.balls), bc.o, y);
      ctx.fillText(String(b.runs), bc.r, y);
      ctx.fillStyle = '#fbbf24';
      ctx.font = FONT(900, 24);
      ctx.fillText(String(b.wickets), bc.w, y);
      ctx.fillStyle = '#cbd5e1';
      ctx.font = FONT(700, 22);
      ctx.fillText(b.balls > 0 ? ((b.runs / b.balls) * 6).toFixed(1) : '—', bc.e, y);
      y += 40;
    });
    y += 40;
  });

  ctx.textAlign = 'center';
  ctx.fillStyle = '#475569';
  ctx.font = FONT(800, 18);
  ctx.fillText('SCORED WITH FFPL CRICKET SCORER', W / 2, H - 46);

  return canvas;
}

/**
 * 1080×1080 "PLAYER OF THE MATCH" card — the viral-growth share. Photo (if
 * the player has a linked profile), this match's batting/bowling lines, the
 * match result, and app branding. Returns a Promise<canvas>.
 * @param {Object} mvp - the computed MVP {name, team, runs, balls, wickets, bowledRuns, bowledBalls, fours, sixes}
 * @param {Object} match - the finished match
 */
export function renderMomCardCanvas(mvp, match) {
  const W = 1080, H = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Stadium-night backdrop: deep navy with a golden glow behind the player.
  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#0c1730');
  grad.addColorStop(0.55, '#111c33');
  grad.addColorStop(1, '#020617');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
  const glow = ctx.createRadialGradient(W / 2, 330, 40, W / 2, 330, 420);
  glow.addColorStop(0, 'rgba(251,191,36,0.28)');
  glow.addColorStop(1, 'rgba(251,191,36,0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  // Laurel-style header
  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  ctx.fillStyle = '#fbbf24';
  ctx.font = FONT(900, 40);
  ctx.fillText('🏆  PLAYER OF THE MATCH  🏆', W / 2, 64);
  ctx.fillStyle = '#64748b';
  ctx.font = FONT(700, 24);
  ctx.fillText(clip(ctx, `${match.teamA.name}  vs  ${match.teamB.name}`, W - 140), W / 2, 122);

  // Photo lookup: linked profile in the local player registry (if any).
  let photo = '';
  try {
    const reg = JSON.parse(localStorage.getItem('cs_player_registry') || '{}');
    const prof = Object.values(reg).find(p => p?.displayName === mvp.name);
    if (prof?.photo) photo = prof.photo;
  } catch (_) {}

  const finish = () => {
    // Name + team
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fff';
    ctx.font = FONT(900, 62);
    ctx.fillText(clip(ctx, mvp.name, W - 160), W / 2, 500);
    ctx.fillStyle = '#94a3b8';
    ctx.font = FONT(800, 28);
    ctx.fillText(clip(ctx, mvp.team || '', W - 200), W / 2, 574);

    // This-match performance tiles (only show what the player actually did)
    const tiles = [];
    if ((mvp.balls || 0) > 0 || (mvp.runs || 0) > 0) {
      tiles.push(['RUNS', `${mvp.runs} (${mvp.balls}b)`]);
      tiles.push(['STRIKE RATE', mvp.balls > 0 ? ((mvp.runs / mvp.balls) * 100).toFixed(0) : '—']);
      if ((mvp.fours || 0) + (mvp.sixes || 0) > 0) tiles.push(['BOUNDARIES', `${mvp.fours || 0}×4  ${mvp.sixes || 0}×6`]);
    }
    if ((mvp.bowledBalls || 0) > 0) {
      tiles.push(['WICKETS', `${mvp.wickets}/${mvp.bowledRuns}`]);
      tiles.push(['OVERS', formatOvers(mvp.bowledBalls)]);
      tiles.push(['ECONOMY', ((mvp.bowledRuns / mvp.bowledBalls) * 6).toFixed(1)]);
    }
    const shown = tiles.slice(0, 3);
    const tw = 292, th = 140, gap = 22;
    const gridW = shown.length * tw + (shown.length - 1) * gap;
    const x0 = (W - gridW) / 2, y0 = 660;
    shown.forEach((t, i) => {
      const x = x0 + i * (tw + gap);
      ctx.fillStyle = 'rgba(30,41,59,0.8)';
      ctx.strokeStyle = 'rgba(251,191,36,0.35)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x, y0, tw, th, 18);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#94a3b8';
      ctx.font = FONT(800, 21);
      ctx.fillText(t[0], x + tw / 2, y0 + 26);
      ctx.fillStyle = '#fbbf24';
      ctx.font = FONT(900, 44);
      ctx.fillText(String(t[1]), x + tw / 2, y0 + 62);
    });

    // Result line
    if (match.result) {
      let line = 'Match tied';
      if (match.result.winner === 'A' || match.result.winner === 'B') {
        const wname = match.result.winner === 'A' ? match.teamA.name : match.teamB.name;
        line = `${wname} won${match.result.margin ? ` by ${match.result.margin} ${match.result.marginType}` : ''}`;
      } else if (match.result.winner === 'COMPLETED') line = 'Match completed';
      ctx.fillStyle = '#34d399';
      ctx.font = FONT(800, 30);
      ctx.fillText(clip(ctx, line, W - 140), W / 2, 856);
    }

    // Score summary line
    const scores = match.innings.map(inn => {
      const tn = inn.battingTeam === 'A' ? match.teamA.name : match.teamB.name;
      return `${tn} ${inn.totalRuns}/${inn.wickets}`;
    }).join('  ·  ');
    ctx.fillStyle = '#64748b';
    ctx.font = FONT(700, 24);
    ctx.fillText(clip(ctx, scores, W - 140), W / 2, 906);

    // Branding footer
    ctx.fillStyle = 'rgba(251,191,36,0.14)';
    ctx.beginPath();
    ctx.roundRect(W / 2 - 300, H - 108, 600, 56, 28);
    ctx.fill();
    ctx.fillStyle = '#fbbf24';
    ctx.font = FONT(900, 24);
    ctx.textBaseline = 'middle';
    ctx.fillText('🏏 SCORED ON FFPL CRICKET SCORER', W / 2, H - 79);
    ctx.textBaseline = 'top';
  };

  return new Promise(resolve => {
    const drawAvatarRing = () => {
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(W / 2, 330, 142, 0, Math.PI * 2);
      ctx.stroke();
    };
    const drawFallbackAvatar = () => {
      drawAvatarRing();
      ctx.fillStyle = '#1e293b';
      ctx.beginPath();
      ctx.arc(W / 2, 330, 128, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#64748b';
      ctx.font = FONT(900, 120);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🏏', W / 2, 338);
      ctx.textBaseline = 'top';
    };
    if (photo) {
      loadImageSafely(photo).then(img => {
        if (img) {
          drawAvatarRing();
          ctx.save();
          ctx.beginPath();
          ctx.arc(W / 2, 330, 128, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(img, W / 2 - 128, 330 - 128, 256, 256);
          ctx.restore();
        } else {
          drawFallbackAvatar();
        }
        finish(); resolve(canvas);
      });
    } else {
      drawFallbackAvatar(); finish(); resolve(canvas);
    }
  });
}

/** 1080×1080 player career stats card. */
export function renderPlayerCardCanvas({ displayName, username, photo }, stats) {
  const W = 1080, H = 1080;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d');

  const grad = ctx.createLinearGradient(0, 0, 0, H);
  grad.addColorStop(0, '#111c33');
  grad.addColorStop(1, '#020617');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Accent ring behind avatar
  ctx.strokeStyle = '#fbbf24';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(W / 2, 195, 105, 0, Math.PI * 2);
  ctx.stroke();

  const finish = () => {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#fff';
    ctx.font = FONT(900, 48);
    ctx.fillText(clip(ctx, displayName || 'Player', W - 160), W / 2, 320);
    if (username) {
      ctx.fillStyle = '#fbbf24';
      ctx.font = FONT(800, 26);
      ctx.fillText('@' + username, W / 2, 375);
    }

    const batAvgStr = stats.outs > 0 ? (stats.runs / stats.outs).toFixed(2) : (stats.runs || 0);
    const srStr = stats.balls > 0 ? ((stats.runs / stats.balls) * 100).toFixed(1) : '—';
    const econStr = stats.ballsBowled > 0 ? ((stats.runsConceded / stats.ballsBowled) * 6).toFixed(2) : '—';
    const bbStr = stats.bestBowlingWickets > 0 ? `${stats.bestBowlingWickets}/${stats.bestBowlingRuns}` : '—';
    const winRateStr = stats.matches > 0 ? `${stats.winRate || 0}%` : '—';

    const tiles = [
      ['MATCHES', stats.matches || 0],
      ['WIN RATE', winRateStr],
      ['MOM AWARDS', stats.momCount || 0],
      ['TOTAL RUNS', stats.runs || 0],
      ['BAT AVG', batAvgStr],
      ['STRIKE RATE', srStr],
      ['HIGH SCORE', stats.highestScore || 0],
      ['50s / 100s', `${stats.fifties || 0} / ${stats.hundreds || 0}`],
      ['WICKETS', stats.wickets || 0],
      ['BEST BOWLING', bbStr],
      ['ECONOMY', econStr],
      ['SIXES', stats.sixes || 0]
    ];
    const cols = 3, tw = 300, th = 110, gap = 16;
    const gridW = cols * tw + (cols - 1) * gap;
    const x0 = (W - gridW) / 2, y0 = 430;
    tiles.forEach((t, i) => {
      const x = x0 + (i % cols) * (tw + gap);
      const y = y0 + Math.floor(i / cols) * (th + gap);
      ctx.fillStyle = 'rgba(30,41,59,0.85)';
      ctx.strokeStyle = 'rgba(148,163,184,0.2)';
      ctx.beginPath();
      ctx.roundRect(x, y, tw, th, 16);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#94a3b8';
      ctx.font = FONT(800, 18);
      ctx.fillText(t[0], x + tw / 2, y + 18);
      ctx.fillStyle = '#fff';
      ctx.font = FONT(900, 38);
      ctx.fillText(String(t[1]), x + tw / 2, y + 48);
    });

    ctx.fillStyle = '#64748b';
    ctx.font = FONT(800, 18);
    ctx.fillText('CAREER STATS · CRICCAM PRO', W / 2, H - 45);
  };

  return new Promise(resolve => {
    if (photo) {
      loadImageSafely(photo).then(img => {
        if (img) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(W / 2, 195, 96, 0, Math.PI * 2);
          ctx.clip();
          ctx.drawImage(img, W / 2 - 96, 195 - 96, 192, 192);
          ctx.restore();
        } else {
          drawFallbackAvatar();
        }
        finish(); resolve(canvas);
      });
    } else {
      drawFallbackAvatar(); finish(); resolve(canvas);
    }
    function drawFallbackAvatar() {
      ctx.fillStyle = '#1e293b';
      ctx.beginPath();
      ctx.arc(W / 2, 195, 96, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#64748b';
      ctx.font = FONT(900, 90);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('🏏', W / 2, 200);
      ctx.textBaseline = 'top';
    }
  });
}
